import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast, Toaster } from "sonner";
import { Loader2, BookOpen, Sparkles, ArrowRight, Frown, Meh, Smile, PartyPopper } from "lucide-react";

const RATINGS_META = [
  { q: 0, key: "missed",  icon: Frown,       tone: "#E5276A" },
  { q: 2, key: "hard",    icon: Meh,         tone: "#F59E0B" },
  { q: 4, key: "ok",      icon: Smile,       tone: "#22C55E" },
  { q: 5, key: "perfect", icon: PartyPopper, tone: "#16A34A" },
];

export default function MicrolearningReview() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(true);
  const [session, setSession] = useState({ good: 0, missed: 0 });
  const [done, setDone] = useState(false);

  const loadQueue = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/microlearning/flashcards/review?limit=5");
      setCards(data?.cards || []);
      setIdx(0); setRevealed(false); setDone(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("review.toastError"));
    } finally { setBusy(false); }
  }, [t]);

  useEffect(() => {
    if (!user) { navigate("/login?next=/microlearning/review"); return; }
    loadQueue();
  }, [user, navigate, loadQueue]);

  const rate = async (quality) => {
    const card = cards[idx];
    if (!card) return;
    try {
      await api.post("/microlearning/flashcards/rate", { flashcard_id: card.id, quality });
      setSession((s) => quality >= 3 ? { ...s, good: s.good + 1 } : { ...s, missed: s.missed + 1 });
      if (idx + 1 < cards.length) { setIdx(idx + 1); setRevealed(false); }
      else { setDone(true); }
    } catch (e) { toast.error(e?.response?.data?.detail || t("review.toastError")); }
  };

  if (busy) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 flex items-center gap-2 text-[#8B95A1]">
        <Loader2 className="w-5 h-5 animate-spin" /> {t("review.loading")}
      </div>
    );
  }

  if (!cards.length) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center" data-testid="review-empty">
        <BookOpen className="w-10 h-10 mx-auto text-[#E5276A]/40 mb-3" />
        <h2 className="font-display text-2xl text-[#1F3A5F]">{t("review.emptyTitle")}</h2>
        <p className="text-sm text-[#4A5568] mt-2">{t("review.emptyBody")}</p>
        <Link to="/play/solo" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
          {t("review.emptyCta")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center" data-testid="review-done">
        <PartyPopper className="w-14 h-14 mx-auto text-[#E5276A]" />
        <h2 className="font-display text-3xl text-[#1F3A5F] mt-4">{t("review.doneTitle")}</h2>
        <p className="text-sm text-[#4A5568] mt-2">
          {t("review.doneBody").replace("{good}", session.good).replace("{total}", session.good + session.missed)}
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button onClick={loadQueue} data-testid="review-restart"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
            {t("review.newSession")} <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/microlearning"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
            {t("review.back")}
          </Link>
        </div>
      </div>
    );
  }

  const card = cards[idx];
  return (
    <div className="max-w-2xl mx-auto px-5 py-8 md:py-14" data-testid="review-page">
      <Toaster theme="light" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{t("review.chip")}</div>
          <h1 className="font-display text-2xl md:text-3xl text-[#1F3A5F]">{t("review.card")} {idx + 1} / {cards.length}</h1>
        </div>
        <div className="text-right text-xs text-[#8B95A1]">
          <div>{t("review.progress").replace("{n}", session.good).replace("{m}", session.missed)}</div>
        </div>
      </div>

      <div className="app-card p-8 min-h-[280px] flex flex-col" data-testid={`review-card-${card.id}`}>
        <div className="text-[11px] uppercase tracking-wider text-[#E5276A] font-bold mb-3">{t("review.question")}</div>
        <div className="font-display text-xl md:text-2xl text-[#1F3A5F] leading-tight flex-1">{card.question}</div>
        {revealed ? (
          <>
            <div className="mt-6 pt-5 border-t border-[#1F3A5F]/8">
              <div className="text-[11px] uppercase tracking-wider text-[#22C55E] font-bold mb-2">{t("review.answer")}</div>
              <div className="text-lg text-[#166534] font-bold">{card.answer}</div>
              {card.explanation && (
                <p className="mt-3 text-sm text-[#4A5568] italic leading-relaxed">
                  <Sparkles className="inline w-3.5 h-3.5 text-[#E5276A] mr-1" /> {card.explanation}
                </p>
              )}
            </div>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-wider text-[#8B95A1] font-bold mb-2">{t("review.howDidYouDo")}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {RATINGS_META.map((r) => (
                  <button key={r.q} onClick={() => rate(r.q)} data-testid={`review-rate-${r.q}`}
                    className="p-3 rounded-xl border-2 hover:scale-[1.02] transition-transform text-center"
                    style={{ borderColor: r.tone, background: `${r.tone}0F` }}>
                    <r.icon className="w-5 h-5 mx-auto mb-1" style={{ color: r.tone }} />
                    <div className="font-bold text-sm" style={{ color: r.tone }}>{t(`review.rate.${r.key}`)}</div>
                    <div className="text-[10px] text-[#8B95A1] leading-tight mt-0.5">{t(`review.rate.${r.key}Desc`)}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <button onClick={() => setRevealed(true)} data-testid="review-reveal"
            className="mt-6 self-start inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
            {t("review.showAnswer")} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between text-xs text-[#8B95A1]">
        <span>{t("review.reviewsCount").replace("{n}", card.review_count || 0)}</span>
        {card.next_review_at && <span>{t("review.nextReview").replace("{date}", new Date(card.next_review_at).toLocaleDateString())}</span>}
      </div>
    </div>
  );
}
